/**
 * Consent Ledger client — thin fetch wrappers for the wallet UI.
 *
 * All endpoints are device-scoped: consumer identifies themselves via
 * the wallet's opaque device_id, no auth required. The magic-link claim
 * token is the only "auth" for binding a pending request to a device.
 */
import axios from "axios";
import { getDeviceId } from "@/lib/drop-off-notifications";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export async function claimConsent(token) {
  const { data } = await axios.post(`${API}/consent/claim`, {
    token,
    device_id: getDeviceId(),
  });
  return data;
}

export async function listConsent(status = null) {
  const params = { device_id: getDeviceId() };
  if (status) params.status = status;
  const { data } = await axios.get(`${API}/wallet/consent`, { params });
  return data;
}

export async function getConsentDetail(consentId) {
  const { data } = await axios.get(`${API}/wallet/consent/${consentId}`, {
    params: { device_id: getDeviceId() },
  });
  return data;
}

export async function decideConsent(consentId, decision, durationDays = null) {
  const body = { device_id: getDeviceId(), decision };
  if (durationDays) body.duration_days = durationDays;
  const { data } = await axios.post(
    `${API}/wallet/consent/${consentId}/decide`,
    body,
  );
  return data;
}

export async function revokeConsent(consentId, reason = null) {
  const body = { device_id: getDeviceId() };
  if (reason) body.reason = reason;
  const { data } = await axios.post(
    `${API}/wallet/consent/${consentId}/revoke`,
    body,
  );
  return data;
}

export async function pendingConsentCount() {
  try {
    const { data } = await axios.get(`${API}/wallet/consent/pending/count`, {
      params: { device_id: getDeviceId() },
    });
    return data.count || 0;
  } catch {
    return 0;
  }
}

// Human labels for enum values used in the UI
export const PURPOSE_LABELS = {
  kyc_verification: "KYC verification",
  affordability_check: "Affordability check",
  product_suitability: "Product suitability review",
  responsible_gaming_onboarding: "Responsible gaming onboarding",
  onboarding: "Account onboarding",
  ongoing_review: "Ongoing account review",
  complaint_handling: "Complaint handling",
  regulatory_reporting: "Regulatory reporting",
  marketing: "Marketing communication",
};

export const CATEGORY_LABELS = {
  identity_basic: "Name, surname & date of birth",
  identity_id_number: "ID number",
  identity_address: "Residential address",
  identity_contact: "Email & phone",
  kyc_verification_result: "KYC verification result",
  affordability_indicators: "Affordability indicators (income & expenses)",
  learning_profile: "Learning history & literacy score",
  product_understanding: "Product understanding score",
  gaming_activity_summary: "Gaming activity summary",
  consumer_assistance_history: "Complaint & assistance history",
};

export const LAWFUL_BASIS_LABELS = {
  consent: "Your consent",
  contract: "Contract with you",
  legal_obligation: "Legal obligation",
  vital_interests: "Vital interests",
  legitimate_interests: "Legitimate interests",
};
